
import { Link } from 'react-router-dom';
import Navbar from '../../../components/feature/Navbar';
import Footer from '../../../components/feature/Footer';
import QuoteForm from '../../../components/feature/QuoteForm';

export default function AsphaltRepair() {
  const repairTypes = [
    {
      title: 'Crack Repair',
      description: 'Hot rubberized crack filling to prevent water infiltration and further damage.',
      icon: 'ri-contrast-drop-2-line',
    },
    {
      title: 'Pothole Patching',
      description: 'Permanent pothole repairs using hot asphalt and proper compaction techniques.',
      icon: 'ri-tools-line',
    },
    {
      title: 'Edge Repair',
      description: 'Restore crumbling driveway edges with new asphalt and proper grading.',
      icon: 'ri-scissors-cut-line',
    },
    {
      title: 'Alligator Cracking',
      description: 'Remove and replace sections with severe interconnected cracking.',
      icon: 'ri-grid-line',
    },
    {
      title: 'Sunken Areas',
      description: 'Repair settled or sunken pavement caused by base failure or erosion.',
      icon: 'ri-arrow-down-circle-line',
    },
    {
      title: 'Infrared Repair',
      description: 'Seamless repairs using infrared heating to blend new and existing asphalt.',
      icon: 'ri-fire-line',
    },
  ];

  const faqs = [
    {
      question: 'How much does asphalt repair cost in Lincoln NE?',
      answer: 'Asphalt repair costs vary by type and extent of damage. Crack filling costs $1-$3 per linear foot. Pothole patching costs $100-$300 per hole. Larger repairs cost $3-$7 per square foot. We provide free estimates with detailed pricing after inspecting your pavement.',
    },
    {
      question: 'Can you repair asphalt in winter?',
      answer: 'Hot asphalt repairs require temperatures above 50°F for proper bonding. In Lincoln winters, we use cold patch for temporary repairs, then return in spring for permanent hot asphalt fixes. Emergency repairs are available year-round for safety hazards.',
    },
    {
      question: 'Should I repair or replace my asphalt driveway?',
      answer: 'Repair if damage covers less than 30% of the surface and the base is stable. Replace if you have extensive cracking, multiple potholes, severe drainage issues, or the pavement is over 20 years old. We assess your driveway and recommend the most cost-effective solution.',
    },
    {
      question: 'How long do asphalt repairs last?',
      answer: 'Properly done repairs last 5-10 years or longer. Hot asphalt patches last longer than cold patch. Crack filling lasts 3-5 years before needing renewal. Infrared repairs blend seamlessly and last as long as surrounding pavement. Regular maintenance extends repair longevity.',
    },
    {
      question: 'What causes asphalt to crack?',
      answer: 'Lincoln\'s freeze-thaw cycles are the primary cause. Water enters small cracks, freezes, expands, and breaks apart the asphalt. UV exposure, oxidation, heavy loads, and poor drainage also contribute. Regular sealcoating and crack filling prevent minor cracks from becoming major problems.',
    },
    {
      question: 'Do you offer emergency asphalt repair?',
      answer: 'Yes! We provide emergency repair services for safety hazards like large potholes, sunken areas, or severe cracking. We respond quickly to assess damage and implement temporary or permanent repairs. Call us for urgent asphalt repair needs in Lincoln NE.',
    },
  ];

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: 'Inter, sans-serif' }}>
      <Navbar />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <p className="text-primary-500 text-sm mb-4 tracking-wider">Services / Repair</p>
            <h1 className="text-5xl md:text-6xl font-black mb-6" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Asphalt Repair Services
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
              Expert asphalt crack repair, pothole patching, and pavement restoration in Lincoln, Nebraska
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
            <div>
              <img
                src="https://public.readdy.ai/ai/img_res/c072fce9e2af163e4858308110e5b742.jpg"
                alt="Lincoln NE asphalt repair services"
                className="w-full h-96 object-cover rounded-2xl shadow-xl"
              />
            </div>
            <div>
              <h2 className="text-4xl font-bold mb-6 text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                Fast, Reliable Asphalt Repairs
              </h2>
              <p className="text-lg text-gray-700 mb-4 leading-relaxed">
                Don't let cracks, potholes, or damaged asphalt compromise your property's safety and appearance. Our expert repair services restore your pavement quickly and affordably, preventing minor issues from becoming expensive replacements.
              </p>
              <p className="text-lg text-gray-700 mb-4 leading-relaxed">
                We specialize in all types of asphalt repairs: crack filling, pothole patching, edge restoration, and infrared repairs. Using professional equipment and quality materials, we deliver lasting results that blend seamlessly with existing pavement.
              </p>
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                Serving Lincoln and Lancaster County homeowners and businesses, we provide same-day estimates and fast turnaround on most repairs. Our experienced crews work efficiently to minimize disruption and get your pavement back in service quickly.
              </p>
              <a href="tel:3084705637" className="inline-block bg-primary-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-primary-700 transition-colors cursor-pointer">
                CALL FOR REPAIR QUOTE
              </a>
            </div>
          </div>

          {/* Repair Types Grid */}
          <div className="mb-16">
            <h2 className="text-4xl font-bold mb-12 text-center text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Repair Services
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {repairTypes.map((repair, index) => (
                <div key={index} className="bg-gray-50 p-8 rounded-2xl border-2 border-gray-200 hover:border-primary-600 transition-all">
                  <div className="w-16 h-16 bg-primary-600 rounded-full flex items-center justify-center mb-6">
                    <i className={`${repair.icon} text-3xl text-white`}></i>
                  </div>
                  <h3 className="text-2xl font-bold mb-4 text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                    {repair.title}
                  </h3>
                  <p className="text-gray-700 leading-relaxed">
                    {repair.description}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Detailed Content */}
          <div className="prose prose-lg max-w-none mb-16">
            <h2 className="text-4xl font-bold mb-6 text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Professional Asphalt Repair in Lincoln NE
            </h2>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Asphalt damage is inevitable in Lincoln's climate. Temperature swings from -10°F to 95°F cause expansion and contraction that creates cracks. Freeze-thaw cycles force water into cracks, where it freezes, expands, and breaks apart the pavement. Without timely repairs, small cracks become potholes, and minor damage becomes major replacement projects.
            </p>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Professional repairs are cost-effective prevention. A $200 crack filling service prevents $2,000 in pothole repairs. A $500 pothole patch prevents $5,000 in pavement replacement. We help property owners protect their investments with timely, quality repairs that extend pavement life by years.
            </p>

            <h3 className="text-3xl font-bold mb-4 mt-8 text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Crack Filling and Sealing
            </h3>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Cracks are asphalt's worst enemy. Even hairline cracks allow water infiltration that undermines the base and causes pavement failure. We use hot rubberized crack filler that remains flexible through temperature extremes, preventing water entry and extending pavement life.
            </p>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Our crack repair process includes cleaning cracks with compressed air or wire brushes, applying hot rubberized filler, and smoothing for a neat finish. For cracks wider than 1/2 inch, we route them out to create a reservoir for better filler adhesion. Crack filling costs $1-$3 per linear foot and should be done every 2-3 years.
            </p>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              We address all crack types: linear cracks (from thermal stress), alligator cracking (from base failure), edge cracks (from poor support), and settlement cracks (from soil movement). Each type requires specific repair techniques for lasting results.
            </p>

            <h3 className="text-3xl font-bold mb-4 mt-8 text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Pothole Patching
            </h3>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Potholes are safety hazards that damage vehicles and create liability. They form when water infiltrates through cracks, weakens the base, and traffic breaks apart the surface. We provide permanent pothole repairs using hot asphalt and proper techniques.
            </p>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Our pothole repair process: remove loose material and debris, square off edges for clean lines, clean the hole thoroughly, apply tack coat for adhesion, fill with hot asphalt in layers, compact each layer, and overfill slightly to account for settling. Proper compaction is critical for lasting repairs.
            </p>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Hot asphalt patches last 5-10 years or longer. Cold patch is available for winter repairs but is temporary (6-12 months). We return in spring to replace cold patches with permanent hot asphalt. Pothole repairs cost $100-$300 depending on size and depth.
            </p>

            <h3 className="text-3xl font-bold mb-4 mt-8 text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Edge Repair and Restoration
            </h3>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Driveway edges are vulnerable to crumbling and deterioration. Without proper support, edges break apart from traffic and weather. We restore edges by removing damaged asphalt, installing edge support (gravel or concrete), and paving with new asphalt that's properly compacted and sealed.
            </p>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Edge repairs prevent further deterioration and improve appearance. We can add concrete curbs for permanent edge protection, or install gravel shoulders for drainage and support. Proper edge support extends driveway life significantly.
            </p>

            <h3 className="text-3xl font-bold mb-4 mt-8 text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Alligator Cracking Repair
            </h3>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Alligator cracking (interconnected cracks resembling alligator skin) indicates base failure. This severe damage requires removing the failed section, repairing the base, and repaving. Simple crack filling won't work because the underlying problem remains.
            </p>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              We saw cut around the damaged area, remove the asphalt, excavate and recompact the base (adding new base material if needed), then pave with hot asphalt that matches the surrounding pavement. This permanent repair addresses the root cause and prevents recurrence.
            </p>

            <h3 className="text-3xl font-bold mb-4 mt-8 text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Sunken Area Repair
            </h3>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Sunken or settled pavement results from base erosion, poor compaction, or soil movement. Water pools in low spots, accelerating deterioration and creating ice hazards. We repair sunken areas by removing the asphalt, addressing the base problem, and repaving to proper grade.
            </p>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              For minor settling (less than 1 inch), we can mill the surrounding area and overlay with new asphalt to level the surface. For severe settling, complete removal and base repair is necessary. Proper drainage prevents future settling.
            </p>

            <h3 className="text-3xl font-bold mb-4 mt-8 text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Infrared Asphalt Repair
            </h3>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Infrared repair is a revolutionary technique that heats existing asphalt to 300-400°F, making it workable again. We add new asphalt, blend it with the heated existing material, and compact for a seamless repair. The result looks and performs like new pavement with no visible seams.
            </p>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Infrared repair is ideal for parking lots, high-visibility areas, and situations where appearance matters. It's faster than traditional methods, uses less material, and creates stronger bonds. Cost is $5-$10 per square foot, competitive with traditional repairs for small areas.
            </p>

            <h3 className="text-3xl font-bold mb-4 mt-8 text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Emergency Repair Services
            </h3>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              We provide emergency asphalt repair for safety hazards and urgent situations. Large potholes, sunken areas, and severe cracking can damage vehicles and create liability. We respond quickly to assess damage and implement repairs that restore safety.
            </p>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Emergency repairs may be temporary (cold patch in winter) or permanent (hot asphalt in warm weather). We work with property owners to address immediate safety concerns, then schedule permanent repairs when conditions allow. Call us for urgent asphalt repair needs in Lincoln NE.
            </p>
          </div>

          {/* Before/After */}
          <div className="mb-16">
            <h2 className="text-4xl font-bold mb-12 text-center text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Repair Results
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <img
                  src="https://readdy.ai/api/search-image?query=damaged%20asphalt%20driveway%20with%20large%20pothole%20and%20cracks%20before%20repair%20showing%20deterioration%20in%20Lincoln%20Nebraska%20with%20clean%20simple%20light%20background&width=800&height=600&seq=37&orientation=landscape"
                  alt="Before asphalt repair"
                  className="w-full h-80 object-cover rounded-2xl shadow-xl"
                />
                <p className="text-center mt-4 text-xl font-semibold text-gray-700">Before Repair</p>
              </div>
              <div>
                <img
                  src="https://readdy.ai/api/search-image?query=repaired%20asphalt%20driveway%20with%20smooth%20patched%20pothole%20and%20sealed%20cracks%20showing%20professional%20restoration%20in%20Lincoln%20Nebraska%20with%20clean%20simple%20light%20background&width=800&height=600&seq=38&orientation=landscape"
                  alt="After asphalt repair"
                  className="w-full h-80 object-cover rounded-2xl shadow-xl"
                />
                <p className="text-center mt-4 text-xl font-semibold text-gray-700">After Repair</p>
              </div>
            </div>
          </div>

          {/* FAQs */}
          <div className="mb-16">
            <h2 className="text-4xl font-bold mb-12 text-center text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Frequently Asked Questions
            </h2>
            <div className="space-y-6">
              {faqs.map((faq, index) => (
                <div key={index} className="bg-gray-50 p-8 rounded-2xl border-2 border-gray-200">
                  <h3 className="text-2xl font-bold mb-4 text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                    {faq.question}
                  </h3>
                  <p className="text-lg text-gray-700 leading-relaxed">
                    {faq.answer}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Quote Form */}
          <div className="bg-gray-50 p-12 rounded-2xl">
            <h2 className="text-4xl font-bold mb-6 text-center text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Get Your Free Repair Quote
            </h2>
            <p className="text-xl text-gray-700 text-center mb-8">
              Fast, professional asphalt repairs throughout Lincoln NE
            </p>
            <QuoteForm />
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
